import { Box, VStack, Text, Input, HStack, Badge } from '@chakra-ui/react';
import { useState, useRef, useEffect } from 'react';
import * as THREE from 'three';
import { getDispatchSuggestions } from '../../core/CommandDispatcher';
import { executeDraftCommand, setAnchorPoint } from '../../core/dynamicInput/dynamicInputEngine';
import { useAppStore } from '../../state/store';
import { useCommandInteract } from '../../core/commands/commandInteract';
import { useDynamicInputStore } from '../../state/useDynamicInputStore';
import { useTranslation } from 'react-i18next';
import { PanelResizeHandle } from './PanelResizeHandle';
import { useCommandLineChrome } from './commandLineChrome';

/**
 * Shared command line panel (the 2D/3D signature console). Extracted
 * from Mode2D so the 3D workbench can type commands too; behaviour
 * is identical: dispatch through executeDraftCommand, feed active command
 * interactions (points/numbers/text), autocomplete, history recall,
 * F2-style scroll log backed by the shared commandLog store.
 */
export function CommandLine({ height = '120px', onResize }: { height?: string; onResize?: (height: number) => void }) {
    const { t } = useTranslation();
    const [command, setCommand] = useState('');
    const commandHistory = useDynamicInputStore((s) => s.commandLog);
    const appendLog = useDynamicInputStore((s) => s.appendLog);
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [inputHistory, setInputHistory] = useState<string[]>([]);
    const interactActive = useCommandInteract((st) => st.active);
    const interactStepIndex = useCommandInteract((st) => st.stepIndex);
    const interactSpec = useCommandInteract((st) => st.spec);
    const prompt = interactActive && interactSpec
        ? (interactSpec.steps[interactStepIndex]?.prompt ?? t('ui.commandLine.command'))
        : t('ui.commandLine.command');

    const inputRef = useRef<HTMLInputElement>(null);
    const executeCommandRef = useRef<(input: string) => void>(() => { });
    const historyRef = useRef<HTMLDivElement>(null);
    const suggestionsRef = useRef<string[]>([]);
    suggestionsRef.current = suggestions;
    // Command line chrome (Faz 11.9): CLIPROMPTLINES pin + fade + opacity.
    const chrome = useCommandLineChrome();

    useEffect(() => {
        if (historyRef.current) {
            historyRef.current.scrollTop = historyRef.current.scrollHeight;
        }
    }, [commandHistory]);

    // Script runner / macro replay: queued 'yap:run-command' events execute
    // through the same dispatch chain as typed input.
    useEffect(() => {
        const onRun = (e: Event) => {
            const input = (e as CustomEvent<{ input: string }>).detail?.input;
            if (input) executeCommandRef.current(input);
        };
        window.addEventListener('yap:run-command', onRun);
        return () => window.removeEventListener('yap:run-command', onRun);
    }, []);

    const executeCommand = (input: string) => {
        executeCommandRef.current = executeCommand;
        if (!input.trim()) {
            if (useCommandInteract.getState().active) {
                useCommandInteract.getState().finishActive();
            }
            return;
        }
        // While a command interaction is active, input feeds the engine:
        // "x,y[,z]" goes to a point step, numbers/text to value steps.
        const interact = useCommandInteract.getState();
        if (interact.active && interact.spec) {
            setInputHistory([...inputHistory, input]);
            setHistoryIndex(-1);
            setCommand('');
            setSuggestions([]);
            const step = interact.spec.steps[interact.stepIndex];
            const newLines: string[] = [`${t('ui.commandLine.commandEcho', { input })}`];
            if (step?.kind === 'point') {
                const parts = input.split(',').map((v) => Number(v.trim()));
                if (parts.length >= 2 && parts.every((v) => Number.isFinite(v))) {
                    const point = new THREE.Vector3(parts[0], parts[1], parts[2] ?? 0);
                    interact.addPoint(point);
                    setAnchorPoint(point);
                    newLines.push(t('ui.commandLine.pointAccepted'));
                } else {
                    newLines.push(t('ui.commandLine.enterCoordinates'));
                }
            } else if (step?.kind === 'pick') {
                newLines.push(t('ui.commandLine.clickObjects'));
            } else if (step) {
                const numeric = Number(input);
                if (step.kind === 'numeric' && Number.isFinite(numeric)) {
                    interact.provideValue(numeric);
                } else {
                    interact.provideValue(input);
                }
            }
            newLines.push('');
            appendLog(newLines);
            return;
        }

        setInputHistory([...inputHistory, input]);
        setHistoryIndex(-1);

        const outcome = executeDraftCommand(input);
        appendLog(outcome.lines);
        setCommand('');
        setSuggestions([]);
    };

    const handleInputChange = (value: string) => {
        // Typing wakes the faded history instantly (Faz 11.9).
        chrome.wake();
        setCommand(value);
        if (value.length >= 1) {
            setSuggestions(getDispatchSuggestions(value));
        } else {
            setSuggestions([]);
        }
    };

    const acceptSuggestion = () => {
        const list = suggestionsRef.current;
        if (list.length === 0) return;
        setCommand(list[0].split(' ')[0]);
        setSuggestions([]);
        inputRef.current?.focus();
    };

    const handleContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!useAppStore.getState().interactionOptions.rightClickCompletesCommand) return;
        if (e.shiftKey) return;
        if (suggestionsRef.current.length > 0) {
            acceptSuggestion();
        } else if (command.trim()) {
            executeCommand(command);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'F1') {
            e.preventDefault();
            useAppStore.getState().toggleOrthoMode();
            return;
        }
        if (e.key === 'Enter') {
            executeCommand(command);
        } else if (e.key === 'Tab' && suggestions.length > 0) {
            e.preventDefault();
            acceptSuggestion();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (inputHistory.length > 0) {
                const newIndex = historyIndex < inputHistory.length - 1 ? historyIndex + 1 : historyIndex;
                setHistoryIndex(newIndex);
                setCommand(inputHistory[inputHistory.length - 1 - newIndex] || '');
            }
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (historyIndex > 0) {
                const newIndex = historyIndex - 1;
                setHistoryIndex(newIndex);
                setCommand(inputHistory[inputHistory.length - 1 - newIndex] || '');
            } else if (historyIndex === 0) {
                setHistoryIndex(-1);
                setCommand('');
            }
        } else if (e.key === 'Escape') {
            useCommandInteract.getState().cancelActive();
            setCommand('');
            setSuggestions([]);
        }
    };

    return (
        <Box
            h={height}
            position="relative"
            style={chrome.panelStyle}
            borderTop="1px solid"
            borderColor="border.base"
            fontFamily="mono"
            fontSize="xs"
            overflow="hidden"
            onClick={() => inputRef.current?.focus()}
            onContextMenu={handleContextMenu}
        >
            <VStack h="100%" spacing={0} align="stretch">
                {/* Command History — CLIPROMPTLINES rows pin above the input,
                    the rest scrolls; the transcript fades COMMANDLINEFADETIME
                    after the last output (Faz 11.9). */}
                <Box
                    ref={historyRef}
                    flex="1"
                    overflowY="auto"
                    p={2}
                    style={chrome.historyStyle}
                    css={{
                        '&::-webkit-scrollbar': { width: '6px' },
                        '&::-webkit-scrollbar-thumb': { background: '#555' },
                    }}
                >
                    {chrome.scrolledLines.map((line, i) => (
                        <Text
                            key={`s-${i}`}
                            color={line.startsWith('Error') ? 'red.400' : line.startsWith('Command:') ? 'brand.500' : 'text.secondary'}
                            lineHeight="1.4"
                        >
                            {line}
                        </Text>
                    ))}
                </Box>
                {chrome.pinnedLines.length > 0 && (
                    <Box px={2} pb={1} style={chrome.historyStyle} flexShrink={0}>
                        {chrome.pinnedLines.map((line, i) => (
                            <Text
                                key={`p-${i}`}
                                color={line.startsWith('Error') ? 'red.400' : line.startsWith('Command:') ? 'brand.500' : 'text.secondary'}
                                lineHeight="1.4"
                            >
                                {line}
                            </Text>
                        ))}
                    </Box>
                )}

                {/* Suggestions */}
                {suggestions.length > 0 && (
                    <HStack px={2} spacing={2} flexWrap="wrap">
                        {suggestions.map((s, i) => (
                            <Badge
                                key={i}
                                colorScheme="blue"
                                cursor="pointer"
                                onClick={() => {
                                    setCommand(s.split(' ')[0]);
                                    setSuggestions([]);
                                    inputRef.current?.focus();
                                }}
                            >
                                {s}
                            </Badge>
                        ))}
                    </HStack>
                )}

                {/* Command Input */}
                <HStack px={2} pb={2} spacing={2} bg="bg.canvas">
                    <Text color={interactActive ? 'yellow.500' : 'brand.500'} fontWeight="bold">{prompt}</Text>
                    <Input
                        ref={inputRef}
                        variant="unstyled"
                        size="sm"
                        value={command}
                        onChange={(e) => handleInputChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={t('ui.commandLine.typeCommand')}
                        color="text.primary"
                        _placeholder={{ color: 'text.secondary' }}
                        fontFamily="mono"
                        autoComplete="off"
                        spellCheck={false}
                    />
                </HStack>
            </VStack>
            {onResize && (
                <PanelResizeHandle
                    currentHeight={parseInt(height, 10) || 120}
                    onResize={onResize}
                />
            )}
        </Box>
    );
}
